import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { notificationsAPI } from '../../services/api';
import { BADGE_REFRESH_EVENT } from '../../services/appBadge';
import SidebarLogo from './SidebarLogo';

/** Precarga chunks de rutas al pasar el mouse — reduce destello al navegar */
const ROUTE_PREFETCH: Record<string, () => Promise<unknown>> = {
  '/dashboard': () => import('../../pages/DashboardPage'),
  '/projects': () => import('../../pages/ProjectsPage'),
  '/tasks': () => import('../../pages/TasksPage'),
  '/sprints': () => import('../../pages/SprintsPage'),
  '/gantt': () => import('../../pages/GanttPage'),
  '/calendar': () => import('../../pages/CalendarPage'),
  '/templates': () => import('../../pages/TemplatesPage'),
  '/bitacora': () => import('../../pages/BitacoraPage'),
  '/reports': () => import('../../pages/ReportsPage'),
  '/portfolio': () => import('../../pages/PortfolioPage'),
  '/approvals': () => import('../../pages/ApprovalsPage'),
  '/crm': () => import('../../pages/CRMPage'),
  '/notifications': () => import('../../pages/NotificationsPage'),
  '/profile': () => import('../../pages/ProfilePage'),
  '/settings': () => import('../../pages/SettingsPage'),
  '/portal-management': () => import('../../pages/PortalManagementPage'),
};

function prefetchRoute(path: string) {
  const key = Object.keys(ROUTE_PREFETCH).find(p => path === p || path.startsWith(p + '/'));
  ROUTE_PREFETCH[key ?? '']?.();
}

// ─── Types ────────────────────────────────────────────────
interface SubItem {
  path: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  allowedRoles?: string[];
}

interface Group {
  id: string;
  label: string;
  icon: React.ReactNode;
  /** Si tiene path directo, no abre flyout */
  directPath?: string;
  allowedRoles?: string[];
  items?: SubItem[];
}

// ─── Sub-item icon helper ─────────────────────────────────
function Icon({ d, d2 }: { d: string; d2?: string }) {
  return (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d={d} />
      {d2 && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d={d2} />}
    </svg>
  );
}

// ─── Nav groups definition ────────────────────────────────
const GROUPS: Group[] = [
  {
    id: 'inicio',
    label: 'Inicio',
    directPath: '/dashboard',
    icon: <Icon d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />,
  },
  {
    id: 'proyectos',
    label: 'Proyectos',
    icon: <Icon d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />,
    items: [
      {
        path: '/projects',
        label: 'Proyectos',
        description: 'Lista y gestión de proyectos',
        icon: <Icon d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />,
      },
      {
        path: '/tasks',
        label: 'Tareas',
        description: 'Tablero Kanban y gestión',
        icon: <Icon d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />,
      },
      {
        path: '/sprints',
        label: 'Sprints',
        description: 'Planificación ágil',
        icon: <Icon d="M13 10V3L4 14h7v7l9-11h-7z" />,
      },
      {
        path: '/gantt',
        label: 'Gantt',
        description: 'Línea de tiempo del proyecto',
        icon: <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />,
      },
      {
        path: '/calendar',
        label: 'Calendario',
        description: 'Tareas por fecha de vencimiento',
        icon: <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />,
      },
      {
        path: '/templates',
        label: 'Plantillas',
        description: 'Proyectos pre-configurados',
        icon: <Icon d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />,
      },
      {
        path: '/bitacora',
        label: 'Bitácora',
        description: 'Registro diario de novedades',
        icon: <Icon d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />,
      },
    ],
  },
  {
    id: 'analisis',
    label: 'Análisis',
    icon: <Icon d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
    items: [
      {
        path: '/reports',
        label: 'Reportes',
        description: 'KPIs y exportación PDF',
        icon: <Icon d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
        allowedRoles: ['admin', 'project_manager'],
      },
      {
        path: '/portfolio',
        label: 'Portafolio',
        description: 'Vista general de proyectos',
        icon: <Icon d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />,
      },
      {
        path: '/approvals',
        label: 'Aprobaciones',
        description: 'Flujos de cambios y presupuesto',
        icon: <Icon d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
      },
    ],
  },
  {
    id: 'negocio',
    label: 'Negocio',
    allowedRoles: ['admin', 'project_manager'],
    icon: <Icon d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />,
    items: [
      {
        path: '/crm',
        label: 'CRM',
        description: 'Contactos y oportunidades',
        icon: <Icon d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />,
        allowedRoles: ['admin', 'project_manager'],
      },
      {
        path: '/portal-management',
        label: 'Portal Cliente',
        description: 'Acceso y reportes para clientes',
        icon: <Icon d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />,
      },
    ],
  },
  {
    id: 'notificaciones',
    label: 'Alertas',
    directPath: '/notifications',
    icon: <Icon d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />,
  },
  {
    id: 'cuenta',
    label: 'Cuenta',
    icon: <Icon d="M5.121 17.804A9 9 0 1118.88 17.8M15 11a3 3 0 11-6 0 3 3 0 016 0z" />,
    items: [
      {
        path: '/profile',
        label: 'Mi Perfil',
        description: 'Datos personales y contraseña',
        icon: <Icon d="M5.121 17.804A9 9 0 1118.88 17.8M15 11a3 3 0 11-6 0 3 3 0 016 0z" />,
      },
      {
        path: '/settings',
        label: 'Configuración',
        description: 'Ajustes del sistema',
        icon: <Icon d="M10.325 4.317a1.724 1.724 0 013.35 0 1.724 1.724 0 002.573 1.066 1.724 1.724 0 012.362.995 1.724 1.724 0 001.725 1.725 1.724 1.724 0 01.995 2.362 1.724 1.724 0 001.066 2.573 1.724 1.724 0 010 3.35 1.724 1.724 0 00-1.066 2.573 1.724 1.724 0 01-.995 2.362 1.724 1.724 0 00-1.725 1.725 1.724 1.724 0 01-2.362.995 1.724 1.724 0 00-2.573 1.066 1.724 1.724 0 01-3.35 0 1.724 1.724 0 00-2.573-1.066 1.724 1.724 0 01-2.362-.995 1.724 1.724 0 00-1.725-1.725 1.724 1.724 0 01-.995-2.362 1.724 1.724 0 00-1.066-2.573 1.724 1.724 0 010-3.35 1.724 1.724 0 001.066-2.573 1.724 1.724 0 01.995-2.362 1.724 1.724 0 001.725-1.725 1.724 1.724 0 012.362-.995 1.724 1.724 0 002.573-1.066z" d2="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />,
      },
    ],
  },
];

// ─── Component ────────────────────────────────────────────
export default function Sidebar({ onClose }: { onClose?: () => void }) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [flyoutVisible, setFlyoutVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeFlyout = useCallback((animate = true) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (!animate) {
      setFlyoutVisible(false);
      setActiveGroup(null);
      return;
    }
    setFlyoutVisible(false);
    closeTimerRef.current = setTimeout(() => setActiveGroup(null), 200);
  }, []);

  useEffect(() => () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }, []);

  const rawRole = (user?.role ?? '').toLowerCase();
  const userRole = rawRole.includes('admin') ? 'admin' : rawRole;

  const canSee = useCallback((roles?: string[]) =>
    !roles || roles.includes(userRole), [userRole]);

  const userRestrictions: string[] = (user as { restrictions?: string[] } | null)?.restrictions ?? [];

  const visibleGroups = GROUPS
    .filter(g => canSee(g.allowedRoles))
    .map(g => {
      if (g.directPath) return g;
      return { ...g, items: g.items?.filter(item => !userRestrictions.includes(item.path)) };
    })
    .filter(g => {
      if (g.directPath) return !userRestrictions.includes(g.directPath);
      return (g.items?.length ?? 0) > 0;
    });

  // Close flyout on outside click
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (
        flyoutRef.current && !flyoutRef.current.contains(e.target as Node) &&
        sidebarRef.current && !sidebarRef.current.contains(e.target as Node)
      ) {
        closeFlyout(true);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Mostrar flyout con fade-in al abrir grupo
  useEffect(() => {
    if (activeGroup) {
      setFlyoutVisible(false);
      requestAnimationFrame(() => setFlyoutVisible(true));
    }
  }, [activeGroup]);

  // Cerrar flyout al cambiar de ruta (con fade)
  useEffect(() => { if (activeGroup) closeFlyout(true); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notification badge
  useEffect(() => {
    let active = true;
    const fetch = async () => {
      try {
        const res = await notificationsAPI.unreadCount();
        if (active) setUnreadCount(res.data?.count ?? 0);
      } catch { /* silent */ }
    };
    const onBadge = (e: Event) => {
      const count = (e as CustomEvent<{ count: number }>).detail?.count;
      if (typeof count === 'number') setUnreadCount(count);
    };
    fetch();
    window.addEventListener(BADGE_REFRESH_EVENT, onBadge);
    const timer = setInterval(fetch, 60_000);
    return () => { active = false; clearInterval(timer); window.removeEventListener(BADGE_REFRESH_EVENT, onBadge); };
  }, []);

  const handleGroupClick = (group: Group) => {
    if (group.directPath) {
      prefetchRoute(group.directPath);
      navigate(group.directPath);
      onClose?.();
      closeFlyout(false);
    } else {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      setActiveGroup(prev => prev === group.id ? null : group.id);
    }
  };

  const handleSubNav = (path: string) => {
    prefetchRoute(path);
    navigate(path);
    closeFlyout(true);
    onClose?.();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isGroupActive = (group: Group) => {
    if (group.directPath) return location.pathname === group.directPath;
    return group.items?.some(i => location.pathname.startsWith(i.path)) ?? false;
  };

  const openGroup = activeGroup ? visibleGroups.find(g => g.id === activeGroup) : null;

  return (
    <>
      {/* ── Main slim sidebar ── */}
      <aside
        ref={sidebarRef}
        className="flex flex-col h-[100dvh] bg-slate-900 border-r border-slate-700/50 w-[100px] flex-shrink-0"
      >
        {/* Logo */}
        <div className="flex items-center justify-center py-4 border-b border-slate-700/50 flex-shrink-0">
          <SidebarLogo collapsed={true} />
        </div>

        {/* Group icons */}
        <nav className="flex-1 flex flex-col items-center gap-1 py-3 px-2 overflow-y-auto">
          {visibleGroups.map(group => {
            const active = isGroupActive(group);
            const flyoutOpen = activeGroup === group.id;
            const isNotif = group.id === 'notificaciones';

            return (
              <button
                key={group.id}
                onClick={() => handleGroupClick(group)}
                title={group.label}
                className={`relative w-[76px] h-[76px] flex flex-col items-center justify-center rounded-xl transition-all duration-150 group gap-1.5 ${
                  active || flyoutOpen
                    ? 'bg-slate-800/90 text-cyan-300 border border-cyan-400/70 shadow-[0_0_0_1px_rgba(34,211,238,0.15),0_0_18px_rgba(34,211,238,0.18)]'
                    : 'text-slate-300 hover:text-cyan-200 hover:bg-slate-800/80 border border-transparent hover:border-cyan-400/40 hover:shadow-[0_0_16px_rgba(34,211,238,0.12)]'
                }`}
              >
                <span className="relative">
                  {group.icon}
                  {isNotif && unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold leading-none">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </span>
                <span className="text-[11px] font-semibold leading-none opacity-70 group-hover:opacity-100 transition-opacity tracking-wide">
                  {group.label}
                </span>
                {/* Active indicator */}
                {(active || flyoutOpen) && (
                  <span className="absolute left-0 top-2 bottom-2 w-0.5 bg-cyan-400 rounded-r-full shadow-[0_0_12px_rgba(34,211,238,0.8)]" />
                )}
              </button>
            );
          })}
        </nav>

        {/* User avatar + logout */}
        <div className="flex flex-col items-center gap-2 p-3 border-t border-slate-700/50 flex-shrink-0">
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br from-amber-400 to-amber-600 text-slate-900 text-lg font-bold">
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <button
            onClick={handleLogout}
            title="Cerrar sesión"
            className="text-red-500/60 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-500/10"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </aside>

      {/* Flyout + Overlay renderizados en document.body via portal
          para escapar el stacking context creado por transition-transform en AppLayout */}
      {openGroup && ReactDOM.createPortal(
        <>
          {/* Overlay */}
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 9000,
              backgroundColor: 'rgba(0,0,0,0.75)',
              opacity: flyoutVisible ? 1 : 0,
              transition: 'opacity 200ms ease',
              pointerEvents: flyoutVisible ? 'auto' : 'none',
            }}
            onClick={() => closeFlyout(true)}
          />

          {/* Flyout panel */}
          {openGroup.items && (
            <div
              ref={flyoutRef}
              style={{
                position: 'fixed', top: 0, left: '100px',
                height: '100dvh', width: '288px',
                zIndex: 9100,
                display: 'flex', flexDirection: 'column',
                backgroundColor: '#0b1120',
                borderRight: '1px solid rgba(34,211,238,0.72)',
                boxShadow: '0 0 0 1px rgba(34,211,238,0.28), 0 0 30px rgba(34,211,238,0.22), 8px 0 52px rgba(0,0,0,0.96)',
                opacity: flyoutVisible ? 1 : 0,
                transform: flyoutVisible ? 'translateX(0)' : 'translateX(-12px)',
                transition: 'opacity 200ms ease, transform 200ms ease',
              }}
            >
              {/* Header */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 20px', flexShrink: 0,
                backgroundColor: '#0b1120',
                borderBottom: '1px solid rgba(34,211,238,0.32)',
                boxShadow: 'inset 0 -1px 0 rgba(34,211,238,0.18)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '36px', height: '36px', borderRadius: '10px',
                    backgroundColor: 'rgba(34,211,238,0.14)', color: '#67e8f9', flexShrink: 0,
                    border: '1px solid rgba(34,211,238,0.5)',
                    boxShadow: '0 0 20px rgba(34,211,238,0.18)',
                  }}>
                    {openGroup.icon}
                  </span>
                  <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '15px', letterSpacing: '0.04em' }}>{openGroup.label}</span>
                </div>
                <button
                  onClick={() => closeFlyout(true)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '30px', height: '30px', borderRadius: '8px',
                    backgroundColor: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.22)', cursor: 'pointer',
                    color: '#a5f3fc', boxShadow: '0 0 10px rgba(34,211,238,0.08)',
                  }}
                >
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Cards grid */}
              <nav style={{
                flex: 1, overflowY: 'auto', padding: '16px',
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px',
                alignContent: 'start',
              }}>
                {openGroup.items
                  .filter(item => canSee(item.allowedRoles))
                  .map(item => {
                    const isActive = location.pathname.startsWith(item.path);
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleSubNav(item.path)}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                          gap: '12px', padding: '16px', borderRadius: '16px',
                          textAlign: 'left', cursor: 'pointer',
                          backgroundColor: isActive ? 'rgba(34,211,238,0.12)' : '#111827',
                          border: isActive ? '1px solid rgba(34,211,238,0.68)' : '1px solid rgba(148,163,184,0.18)',
                          color: isActive ? '#a5f3fc' : '#e2e8f0',
                          boxShadow: isActive ? '0 0 0 1px rgba(34,211,238,0.22), 0 0 20px rgba(34,211,238,0.18)' : '0 0 0 1px rgba(148,163,184,0.06)',
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={e => {
                          prefetchRoute(item.path);
                          if (!isActive) {
                            const el = e.currentTarget as HTMLElement;
                            el.style.backgroundColor = 'rgba(34,211,238,0.08)';
                            el.style.borderColor = 'rgba(103,232,249,0.72)';
                            el.style.boxShadow = '0 0 0 1px rgba(103,232,249,0.18), 0 0 22px rgba(34,211,238,0.18)';
                          }
                        }}
                        onMouseLeave={e => {
                          if (!isActive) {
                            const el = e.currentTarget as HTMLElement;
                            el.style.backgroundColor = '#111827';
                            el.style.borderColor = 'rgba(148,163,184,0.18)';
                            el.style.boxShadow = '0 0 0 1px rgba(148,163,184,0.06)';
                          }
                        }}
                      >
                        <span style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                          backgroundColor: isActive ? 'rgba(34,211,238,0.16)' : 'rgba(148,163,184,0.08)',
                          color: isActive ? '#67e8f9' : '#cbd5e1',
                          border: isActive ? '1px solid rgba(34,211,238,0.52)' : '1px solid rgba(148,163,184,0.14)',
                          boxShadow: isActive ? '0 0 18px rgba(34,211,238,0.14)' : '0 0 0 rgba(0,0,0,0)',
                        }}>
                          {item.icon}
                        </span>
                        <div>
                          <p style={{ fontSize: '13px', fontWeight: 700, lineHeight: '1.2', margin: 0, color: isActive ? '#e0f2fe' : '#f8fafc' }}>{item.label}</p>
                          <p style={{ fontSize: '11px', color: isActive ? '#a5f3fc' : '#94a3b8', marginTop: '4px', lineHeight: '1.4' }}>{item.description}</p>
                        </div>
                      </button>
                    );
                  })}
              </nav>

              {/* Footer */}
              <div style={{
                padding: '12px 16px', flexShrink: 0,
                borderTop: '1px solid rgba(34,211,238,0.22)',
                backgroundColor: 'rgba(15,23,42,0.8)',
                boxShadow: 'inset 0 1px 0 rgba(34,211,238,0.09)',
              }}>
                <p style={{ color: '#f8fafc', fontSize: '12px', fontWeight: 700, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</p>
                <p style={{ color: '#67e8f9', fontSize: '11px', textTransform: 'capitalize', margin: 0, letterSpacing: '0.04em' }}>{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          )}
        </>,
        document.body,
      )}
    </>
  );
}
